import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _TypeUtils;
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

// Union of all primitive types

// Can't easily make a PrimitiveTypeofString, as `typeof null` returns "object"
// Union of all typeof strings

// Utility type for comparisons. Follows the JS standard: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#comparefn

// This can hopefully be removed in TS 5.4
// https://devblogs.microsoft.com/typescript/announcing-typescript-5-4-beta/#the-noinfer-utility-type
// https://stackoverflow.com/questions/56687668
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint

/*
  Returns object type T, modified by removing optional, undefined, and null attributes from properties specified by K<br>
  T: an object type
  K: a union of string literals that are property keys of T
 */

// Awaited is native in TS 4.5. This is the copied implementation and should be removed on upgrade to 4.5.
// Note that later versions of TS also address issues with types like this:
// In later versions, Awaited<Promise<T>> is T. In 4.2, it is not.

export class TypeUtils {
  constructor() {
    // static class, can't create an instance
  }

  /**
   * Converts the given primitive to a unique string to compare against other primitives and be
   * used as a key. If two primitives that are triple equal are passed in, identical keys will be generated.
   * If two primitives that are not triple equal, their keys will be different (EXCEPT in the case of symbols
   * generated using the Symbol() constructor).
   * @param primitive
   */

  /**
   * Generates a unique ID of the form abid-<ID_INDEX> or <prefix>-abid-<ID_INDEX>
   * @param prefix Optional prefix for unique ID.
   */
  static generateUniqueId(prefix) {
    return `${prefix ? `${prefix}-` : ""}abid-${TypeUtils.ID_INDEX++}`;
  }
}

/**
 * Return union of all keys in Base that extend a given Condition (aka type).
 * See https://www.piotrl.net/typescript-condition-subset-types/
 */
_TypeUtils = TypeUtils;
_defineProperty(TypeUtils, "keyMap", new WeakMap());
_defineProperty(TypeUtils, "ID_INDEX", 1);
_defineProperty(TypeUtils, "primitiveToKeyString", primitive => {
  return `${primitive === null ? "null" : typeof primitive}_${typeof primitive === "symbol" ? primitive.toString() : primitive}`;
});
/**
 * Type guard for PrimitiveType.
 * @param item
 */
_defineProperty(TypeUtils, "isPrimitive", item => {
  return item === null || typeof item !== "object" && typeof item !== "function";
});
/**
 * Takes in an item and maps it to a key. If no keyFunction is provided, it is auto-generated.
 * @param item
 * @param keyFunction
 */
_defineProperty(TypeUtils, "itemToKey", (item, keyFunction) => {
  if (keyFunction) {
    return "c_" + keyFunction(item);
  }
  return "a_" + _TypeUtils.keyFunction(item);
});
_defineProperty(TypeUtils, "equals", (value, item, keyFunction) => {
  // keyFunction when ItemT is non-primitive and contains undefined must map undefined to a unique value so this
  // check works as desired. If it were not unique, then we'd need the following case and there's no simple way to
  // do that in a typesafe manner.
  if (value === undefined) {
    return value === item;
  }
  return _TypeUtils.itemToKey(value, keyFunction) === _TypeUtils.itemToKey(item, keyFunction);
});
_defineProperty(TypeUtils, "keyFunction", value => {
  switch (typeof value) {
    case "object":
    case "function":
      if (value === null) {
        return _TypeUtils.primitiveToKeyString(value);
      }
      const key = _TypeUtils.keyMap.get(value);
      if (key !== undefined) {
        return key;
      } else {
        const newKey = _TypeUtils.generateUniqueId("abkey");
        _TypeUtils.keyMap.set(value, newKey);
        return newKey;
      }
    case "bigint":
    case "number":
    case "string":
    case "boolean":
    case "undefined":
    case "symbol":
      return _TypeUtils.primitiveToKeyString(value);
  }
});
/**
 * type guard to test whether a member of an object is defined and narrow the type of the object accordingly<br>
 * If true is returned, refines type of 'obj' so that member 'keyName' is not optional, not undefined, and not null
 * Useful if you want to test value of a member to confirm you can pass type into related type that requires that member.
 * Or if you want to rely on that being defined farther down.
 *
 * <pre>
 *   type AOptional = {a?: number };
 *   type A = {a: number }
 *   const opt: AOptional;
 *   // this code fails to compile. Compiler doesn't narrow type of opt.
 *   if (opt.a != null) { const a: A = opt; }
 *   // this code compiles, because memberIsDefined narrows the type
 *   if (memberIsDefined(opt, "a")) { const a: A = opt; }
 *   </pre>
 * @returns true if keyName of obj is defined and non-null (!= null)
 * @param obj
 * @param keyName
 */
_defineProperty(TypeUtils, "propertyIsDefined", (obj, keyName) => {
  return obj[keyName] != null;
});
/**
 * Empty stub function that will always be triple equal to itself, used to avoid componentWillReceiveProps issues.
 */
_defineProperty(TypeUtils, "emptyFunction", () => {});
_defineProperty(TypeUtils, "isNotNullishOrFalse", item => {
  return item !== null && item !== undefined && item !== false;
});
_defineProperty(TypeUtils, "filterOutNullishOrFalse", items => {
  return items.filter(_TypeUtils.isNotNullishOrFalse);
}); // FYI - If Base is the imported module (aka () => Promise<Base>),
// then Condition only appears to work if it is a primitive type.
// So can't fine all module exports that implement an interface or extend a class...
/*
 * This recursive type ensures that all objects and their properties, as well as arrays and maps,
 * are read-only.
 */
/*
 * This recursive type ensures that all objects and their properties, as well as arrays and maps,
 * are writeable.
 */
/*
 * Got an interface with some optional values, or some required values that can assume `null | undefined`?
 * Would you like to make one or more of these values both required and defined?
 * This might be the type for you.
 *
 * This works by partitioning `keyof T`, the keys of the interface, into two complementary sets:
 * - `K`, the set of keys you'd like to operate on
 * - `Exclude<keyof T, K>`, all other keys, which will remain unchanged
 *
 * For each key in `K`, remove (subtract) its optionality with `-?`. Furthermore, ensure that the value for such a key
 * cannot assume `null | undefined`.
 */
// `Exclude<keyof T, K>`, included but unchanged
//# sourceMappingURL=TypeUtils.js.map
